import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { NavigationFlowHandler } from '@adapters/navigation/navigationFlowHandler';
import { ProcessPage, ProcessPageNavigation } from '@adapters/navigation/page';
import { Router } from '@angular/router';
import { Bank } from '@core/model/domain/bank';
import { Logger } from '@core/model/domain/logger';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { Option } from '@core/model/framework/Result/Option';
import { Result } from '@core/model/framework/Result/Result';
import {
  ConnectionFormItemDto,
  RegionalBankPayload,
} from '@core/service/digital-factory/bank/types/dto/connectionFormDto';
import { AuthenticateToBankWithCredentials } from '@core/usecase/authenticateToBankWithCredentials';
import { GetAccounts } from '@core/usecase/getAccounts';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { RedirectExternalPageSelectedBank } from '@modules/redirect-to-external-bank/redirect-to-external-bank.component';
import {
  AbandonEvent,
  AcceptedProcessEvent,
  AccountsFoundEvent,
  AccountsSelectedEvent,
  BankChoiceEvent,
  ConsentEvent,
  ContinueAfterErrorEvent,
  ContinueEvent,
  GoBackToBankChoiceEvent,
  InternalErrorEvent,
  ParentProcessEvent,
  ProcessEventFactory,
  ProcessEventType,
  RetryAccountAnalysisEvent,
  WebauthWithRegionalBank,
} from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { AbortCase } from '@core/usecase/abortCase';
import { FailCase } from '@core/usecase/failCase';
import { GetBankById } from '@core/usecase/getBankById';
import { ConnectionStateEnum } from '@core/model/domain/bankConnection';
import { Buffer } from 'buffer';

export class ProcessVM {
  isWebauthRedirectionGoingToPartner = false;

  constructor(
    private readonly processEventService: ProcessEventService,
    private readonly storeService: StoreService,
    private readonly navigationFlowHandler: NavigationFlowHandler,
    private readonly broadcastService: BrowserBroadcast,
    private readonly abortCase: AbortCase,
    private readonly failCase: FailCase,
    private readonly logger: Logger,
    private readonly router: Router,
    private readonly authenticateToBankWithCredentials: AuthenticateToBankWithCredentials,
    private readonly getAccounts: GetAccounts,
    private readonly isTrackingEnabled: boolean,
    private readonly tracker: TrackerService,
    private readonly notificationSwitcher: NotificationSwitcher,
    private readonly saveWebauthBankConnection: SaveWebauthBankConnection,
    private readonly getBankById: GetBankById
  ) {}

  async initUserJourney() {
    this.processEventService.subscribe(this.handleEvent.bind(this));
    this.navigationFlowHandler.handleProcessPageRefresh();

    await this.initServices();

    const journeyType = this.storeService.getVariableOrThrow('JOURNEY_TYPE');
    this.isWebauthRedirectionGoingToPartner = this.storeService.getVariableOrThrow(
      'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER'
    );

    if (journeyType.type === 'NEW_USER') {
      this.handleNewUserJourney();
      return;
    }

    if (journeyType.type === 'WEBAUTH_SUCCESS') {
      await this.handleUserSucceededWebauth();
      return;
    }

    if (journeyType.type === 'WEBAUTH_FAILED') {
      this.handleUserFailedWebauth();
      return;
    }
  }

  private async handleEvent(event: AcceptedProcessEvent | ParentProcessEvent) {
    switch (event.type) {
      case ProcessEventType.CONSENT:
        this.handleConsent(event);
        break;
      case ProcessEventType.BANK_CHOICE:
        await this.handleBankChoice(event);
        break;
      case ProcessEventType.GO_BACK_TO_BANK_CHOICE:
        this.handleBackToBankChoice(event);
        break;
      case ProcessEventType.ACCOUNTS_FOUND:
        this.handleAccountsFoundEvent(event);
        break;
      case ProcessEventType.CONTINUE_AFTER_ERROR:
        this.handleContinueAfterError(event);
        break;
      case ProcessEventType.ABANDON:
        await this.handleAbandon(event);
        break;
      case ProcessEventType.ACCOUNTS_SELECTED:
        this.handleAccountsSelected(event);
        break;
      case ProcessEventType.CONTINUE:
        this.handleContinueEvent(event);
        break;
      case ProcessEventType.INTERNAL_ERROR:
        await this.handleInternalErrorEvent(event);
        break;
      case ProcessEventType.ASK_TO_RETRY_WEBAUTH:
        this.handleAskToRetryWebauth();
        break;
      case ProcessEventType.WEBAUTH_WITH_REGIONAL_BANK:
        this.handleWebauthWithRegionalBank(event);
        break;
      case ProcessEventType.RETRY_ACCOUNT_ANALYSIS:
        this.handleRetryAccountAnalysis(event);
        break;
      case ProcessEventType.CREDENTIALS_AUTHENTICATION:
        this.handleCredentialsAuthentication(event.payload);
        break;
      case ProcessEventType.RETRY_BANK_AUTHENTICATION:
        this.handleRetryBankAuthentication();
        break;
      case ProcessEventType.WINDOW_RESIZE_EVENT:
        break;
      default:
        this.neverCase(event);
    }
  }

  public neverCase(_input: never): never {
    throw new Error('should never go here');
  }

  private handleConsent(_consentEvent: ConsentEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  private handleRetryAccountAnalysis(_event: RetryAccountAnalysisEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  private async handleBankChoice(bankChoiceEvent: BankChoiceEvent) {
    const selectedBank = bankChoiceEvent.data;
    this.storeService.setVariable('SELECTED_BANK', selectedBank);

    const hasRegionalBanks = selectedBank.form.length !== 0;
    if (hasRegionalBanks) {
      this.navigateToProcessPage({ page: 'SELECT_REGIONAL_BANK', params: null });
      return;
    }

    this.handleWebauthAuthentication({ regionalBankPayload: null, selectedBank });
  }

  private handleWebauthWithRegionalBank(event: WebauthWithRegionalBank) {
    const regionalBankPayload = { value: event.regionalBankPayload };
    const selectedBank = this.storeService.getVariableOrThrow('SELECTED_BANK');

    this.handleWebauthAuthentication({ regionalBankPayload, selectedBank });
  }

  private handleBackToBankChoice(_event: GoBackToBankChoiceEvent) {
    this.navigationFlowHandler.handleGoBackViaInAppButton();
  }

  private handleAccountsFoundEvent(event: AccountsFoundEvent) {
    this.storeService.setVariable('FOUND_ACCOUNTS', event.data);
    const nbAccounts = event.data.length;

    if (!nbAccounts) {
      this.navigateToProcessPage({ page: 'NONE_ACCOUNTS', params: null });
      return;
    }

    this.navigateToProcessPage({ page: 'SELECT_ACCOUNTS', params: null });
  }

  private handleAccountsSelected(event: AccountsSelectedEvent) {
    this.storeService.setVariable('SELECTED_ACCOUNTS', event.selectedAccounts);
    this.navigateToProcessPage({ page: 'ACCOUNTS_ANALYSIS', params: null });
  }

  private async handleAbandon(_event: AbandonEvent) {
    const abortInput = {
      userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      userId: this.storeService.getVariableOrThrow('USER_ID'),
    };

    try {
      await this.abortCase.execute(abortInput);
      this.storeService.reset();
      this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: false });
      window.parent.postMessage('ABORTED', '*');
    } catch {
      await this.failCase.execute(abortInput);
      this.storeService.reset();
      this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: false });
      window.parent.postMessage('ERROR', '*');
    }
  }

  private handleContinueEvent(_event: ContinueEvent) {
    this.storeService.reset();
    this.logger.logEvent('END_OF_SEQUENCE', { success: true, hasErrors: false });
    window.parent.postMessage('SUCCESS', '*');
  }

  private handleContinueAfterError(_event: ContinueAfterErrorEvent) {
    this.storeService.reset();
    this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: true });
    window.parent.postMessage('ERROR', '*');
  }

  private async handleInternalErrorEvent(event: InternalErrorEvent) {
    this.logger.logEvent('Internal error event', event.payload);

    await this.failCase.execute({
      userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      userId: this.storeService.getVariableOrThrow('USER_ID'),
    });

    this.navigateToErrorRoute();
  }

  private handleAskToRetryWebauth() {
    const processPage = this.storeService.getVariableOrThrow('PROCESS_PAGE');
    const hasUserLeftWaitOnExternalBankLoginPage = processPage !== 'WAIT_ON_EXTERNAL_BANK_LOGIN';

    if (!hasUserLeftWaitOnExternalBankLoginPage) {
      this.navigateToProcessPage({
        page: 'BANK_CONNECTION_FAILURE_RETRY',
        params: null,
      });
    }
  }

  private navigateToProcessPage<T extends ProcessPage>(processPageNavigation: ProcessPageNavigation<T>) {
    this.storeService.setVariable('PROCESS_PAGE', processPageNavigation.page);
    this.storeService.setVariable('PROCESS_PAGE_PARAMS', processPageNavigation.params);
    window.scrollTo({ top: 0 });

    this.navigationFlowHandler.handleProcessNavigation(processPageNavigation.page);
  }

  private navigateToErrorRoute() {
    this.router.navigate(['error'], { state: { isWrongUrl: false } });
  }

  private async handleCredentialsAuthentication({
    form,
    selectedBank,
  }: {
    form: ConnectionFormItemDto[];
    selectedBank: Bank;
  }) {
    this.navigateToProcessPage({ page: 'LOADING', params: null });
    const userId = this.storeService.getVariableOrThrow('USER_ID');
    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');

    try {
      const authenticateResult = await this.authenticateToBankWithCredentials.execute({
        bankId: selectedBank.uid,
        form,
        userId,
        userToken,
      });

      const bankConnection = Result.unwrap(authenticateResult);
      const accountsResult = await this.getAccounts.execute({
        connectionId: bankConnection.connectionId,
        userId,
        userToken,
      });
      const accounts = Result.unwrap(accountsResult);

      this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
      this.storeService.setVariable('SELECTED_CONNECTION_ID', bankConnection.connectionId);
    } catch (_e) {
      this.processEventService.push(
        ProcessEventFactory.SpawnInternalErrorEvent({
          error: 'Error during authentication with credentials authentication',
          errorOrigin: 'ProcessComponent',
        })
      );
    }
  }

  private handleWebauthKO() {
    this.broadcastService.getMessageFromLabel('CONNECTION_EXTERNAL_BANK_KO').subscribe(() => {
      const isWebauthCancelled =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      if (!isWebauthCancelled) {
        this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth());
      }
    });
  }

  private handleWebauthHasUserStoppedWaitingCheck() {
    this.broadcastService.getMessageFromLabel('WEBAUTH_POPUP_REACHED').subscribe(() => {
      const hasUserStoppedWaiting =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      this.broadcastService.publish({
        label: 'WEBAUTH_POPUP_ANSWER_HAS_USER_STOPPED_WAITING',
        data: { hasUserStoppedWaiting },
      });
    });
  }

  private handleRetryBankAuthentication() {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  /*
    Need to be initialized here in case of refresh
  */
  private async initServices() {
    await this.initializeTaggingTracker();
    await this.initializeNotificationSwitcher();
  }

  private handleWebauthAuthentication({
    regionalBankPayload,
    selectedBank,
  }: {
    regionalBankPayload: Option<RegionalBankPayload>;
    selectedBank: Bank;
  }) {
    /* 
      In order to open the popup without asking the user for authorization,
      we need to open the popup after an explicit user interaction (click).
      There should be no delay and no asynchronous operation, otherwise,
      it's not considered as an explicit user interaction.

      Thus : 
        1. Open new tab
        2. Add content to new tab to inform user we wait external bank url
        3. Redirect to webauth url
    */
    const parsedUrl = new URL(window.location.href);
    const baseUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${parsedUrl.port ? `:${parsedUrl.port}` : ''}`;

    const redirectSelectedBank: RedirectExternalPageSelectedBank = {
      logo: selectedBank.logo ? { value: selectedBank.logo } : null,
      name: selectedBank.name,
      uid: selectedBank.uid,
    };
    const redirectSelectedBankStringified = Buffer.from(JSON.stringify(redirectSelectedBank)).toString(
      'base64'
    );
    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');
    const tagging = this.storeService.getVariable('TAGGING_INIT_DATA_IN_BASE64');
    const regionalBankPayloadStringified = Buffer.from(JSON.stringify(regionalBankPayload)).toString(
      'base64'
    );

    let queryParams = `?regionalBank=${regionalBankPayloadStringified}&selectedBank=${redirectSelectedBankStringified}&userToken=${userToken}`;

    if (tagging != null) {
      queryParams += `&tagging=${tagging}`;
    }

    const webauthTab = window.open(`${baseUrl}/redirect-to-external-bank${queryParams}`);

    this.navigateToProcessPage({
      page: 'WAIT_ON_EXTERNAL_BANK_LOGIN',
      params: { regionalBankName: regionalBankPayload?.value.name },
    });

    if (!this.isWebauthRedirectionGoingToPartner) {
      this.broadcastService.getMessageFromLabel('CLOSE_WEBAUTH_POPUP').subscribe(() => {
        webauthTab?.close();
      });

      this.broadcastService.getMessageFromLabel('REDIRECT_TO_EXTERNAL_BANK_LOGIN_ERROR').subscribe(() => {
        const processPage = this.storeService.getVariableOrThrow('PROCESS_PAGE');
        const hasUserLeftWaitOnExternalBankLoginPage = processPage !== 'WAIT_ON_EXTERNAL_BANK_LOGIN';

        if (!hasUserLeftWaitOnExternalBankLoginPage) {
          this.navigateToProcessPage({
            page: 'BANK_CONNECTION_FAILURE_RETRY',
            params: null,
          });
        }
      });
    }
  }

  private handleNewUserJourney() {
    if (!this.isWebauthRedirectionGoingToPartner) {
      this.handleWebauthKO();
      this.handleWebauthHasUserStoppedWaitingCheck();
    }

    if (this.storeService.getVariable('PROCESS_PAGE') == null) {
      this.navigateToProcessPage({ page: 'CONSENT', params: null });
    }
  }

  private async initializeTaggingTracker() {
    const taggingInitDataInBase64 = this.storeService.getVariable('TAGGING_INIT_DATA_IN_BASE64');

    if (this.isTrackingEnabled) {
      if (taggingInitDataInBase64 == null) {
        this.logger.logTrace('ERROR - No tagging provided - Tagging tracker not initialized');
      } else {
        await this.tracker.initializeTaggingTracker(taggingInitDataInBase64);
      }
    }
  }

  private async initializeNotificationSwitcher() {
    await this.notificationSwitcher.initialize();
  }

  private async handleUserSucceededWebauth() {
    try {
      this.displayLoading();
      const userId = this.storeService.getVariableOrThrow('USER_ID');
      const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');
      const userJourney = this.storeService.getVariableOrThrow('JOURNEY_TYPE');

      if (userJourney.type != 'WEBAUTH_SUCCESS') {
        throw new Error('User must be in webauth success state ');
      }

      const bankConnectionResult = await this.saveWebauthBankConnection.execute({
        webauthPopupUserToken: userToken,
        webauthPopupConnectionId: userJourney.payload.externalConnectionId,
      });
      const { connectionId, bankId, state } = Result.unwrap(bankConnectionResult);

      if (state !== ConnectionStateEnum.VALID) {
        this.handleUserFailedWebauth();
        this.logger.logException(new Error(`webauth-connection state is not valid. State is ${state}`));
        return;
      }

      this.storeService.setVariable('SELECTED_CONNECTION_ID', connectionId);

      const bankResult = await this.getBankById.execute({ bankId });
      const bank = Result.unwrap(bankResult);
      this.storeService.setVariable('SELECTED_BANK', bank);

      const activeWaiter = new Promise((resolve) => {
        setTimeout(() => {
          resolve(undefined);
        }, 1000);
      });
      await activeWaiter;

      const result = await this.getAccounts.execute({ connectionId, userId, userToken });
      const accounts = Result.unwrap(result);
      this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
    } catch (e) {
      this.logger.logException(e);
      this.navigateToProcessPage({ page: 'BANK_CONNECTION_FAILURE_RETRY', params: null });
    }
  }

  private handleUserFailedWebauth() {
    this.displayLoading();
    this.navigateToProcessPage({ page: 'BANK_CONNECTION_FAILURE_RETRY', params: null });
  }

  private displayLoading() {
    this.storeService.setVariable('PROCESS_PAGE', 'LOADING');
  }
}
